import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Chart from "react-apexcharts";
import { getMilestoneTrend } from "../../api/process";

export default function MTAChart() {
    const [milestones, setMilestones] = useState([]);
    const [projectPeriod, setProjectPeriod] = useState({ start: 0, end: 0 });
    const { id: projectId } = useParams();

    useEffect(() => {
        getMilestoneTrend().then((res) => {
            setMilestones(res.milestones || []);
            setProjectPeriod({
                start: new Date(res.project.period_start).getTime(),
                end: new Date(res.project.period_finish).getTime(),
            });
        });
    }, [projectId]);

    if (!milestones.length) return <p>로딩 중...</p>;

    // ✅ 각 milestone 시리즈 (점 제거, 대각선 이후 구간 제외)
    const milestoneSeries = milestones.map((ms) => {
        const points = ms.updates
            .map((u) => ({
                x: u.cutoff === 0 ? projectPeriod.start : new Date(u.cutoff).getTime(),
                y: new Date(u.forecast).getTime(),
            }))
            .sort((a, b) => a.x - b.x);

        const clipped = [];

        for (let i = 0; i < points.length; i++) {
            const curr = points[i];
            if (i === 0) {
                clipped.push(curr);
                continue;
            }

            const prev = points[i - 1];

            // ⚙️ 아직 대각선 위라면 그대로 추가
            if (curr.y >= curr.x) {
                clipped.push(curr);
                continue;
            }

            // ⚙️ 대각선(y=x)을 지난 경우 → 교차점 계산 후 끊기
            const t = (prev.x - prev.y) / ((curr.y - prev.y) - (curr.x - prev.x));
            const x_cross = prev.x + (curr.x - prev.x) * t;
            const y_cross = x_cross; // y=x이므로 동일
            clipped.push({ x: x_cross, y: y_cross });
            break; // ✅ 교차점 이후 데이터는 버림
        }

        return { name: ms.name, data: clipped };
    });


    // ✅ 대각선 (Completion Line, y = x)
    const completionLine = {
        name: "Completion Line",
        data: [
            { x: projectPeriod.start, y: projectPeriod.start },
            { x: projectPeriod.end, y: projectPeriod.end },
        ],
        // color: "#ef4444",
    };

    const palette = ["#3b82f6", "#22c55e", "#f97316", "#a855f7", "#06b6d4", "#0ea5e9"];
    const colors = [
        ...milestoneSeries.map((_, i) => palette[i % palette.length]),
        "#ef4444", // 마지막 대각선은 빨강
    ];

    // ✅ 공통 설정
    const options = {
        chart: {
            type: "line",
            animations: { enabled: false },
            zoom: { enabled: false }, // 정사각형 유지 위해 비활성화
            toolbar: { show: false },
        },
        // ⚙️ 선만 표시 (점 제거)
        markers: { size: 0 },
        // ⚙️ 정사각형 유지 (비율 고정)
        grid: {
            padding: { left: 10, right: 10 },
            xaxis: { lines: { show: true } },
            yaxis: { lines: { show: true } },
        },
        stroke: {
            width: [
                ...milestoneSeries.map(() => 1), // 마일스톤 선 얇게
                2, // 대각선 살짝 두껍게
            ],
            curve: "straight",
            dashArray: [
                ...milestoneSeries.map(() => 0), // 실선
                0, // 대각선도 실선
            ],
        },
        colors:
            colors
        ,
        legend: { show: true, position: "bottom" },
        xaxis: {
            type: "datetime",
            min: projectPeriod.start,
            max: projectPeriod.end,
            tickAmount: 10,
            title: { text: "Cut-off Date" },
            labels: {
                formatter: (val) => {
                    const d = new Date(val);
                    return `${String(d.getFullYear()).slice(2)}/${String(
                        d.getMonth() + 1
                    ).padStart(2, "0")}`;
                },
            },
        },
        yaxis: {
            type: "datetime",
            min: projectPeriod.start,
            max: projectPeriod.end,
            tickAmount: 10,
            title: { text: "Forecast Finish Date" },
            labels: {
                formatter: (val) => {
                    const d = new Date(val);
                    return `${String(d.getFullYear()).slice(2)}/${String(
                        d.getMonth() + 1
                    ).padStart(2, "0")}`;
                },
            },
        },
        tooltip: {
            enabled: true,
            x: { format: "yyyy-MM-dd" },
            y: {
                formatter: (val) =>
                    new Date(val).toLocaleDateString("ko-KR", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    }),
            },
        },
        legend: { show: false },
    };

    return (
        <div className="p-4 bg-white rounded-xl shadow flex justify-center">
            {/* ✅ width/height 동일로 정사각형 차트 */}
            <Chart
                options={options}
                series={[...milestoneSeries, completionLine]}
                type="line"
                width={1400}
            // height={800}
            />
        </div>
    );
}
